import type { ComponentType, RefAttributes } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconCalendarCheck,
  IconCheck,
  IconClock,
  IconHome,
  IconKey,
  type IconProps,
  IconSearch,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { datadogRum } from "@datadog/browser-rum";
import Button from "../UI/Button";
import { useToast } from "../UI/Toast/useToast";
import Text, { type Color } from "../UI/Text";
import Title from "../UI/Title";
import Status from "../UI/Status";
import StepBadge from "../UI/StepBadge";
import {
  STEP_DESC_COLORS,
  STEP_TITLE_COLORS,
  type StepStatus,
} from "../UI/stepStatus";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { RumActions } from "../../utils/constants/rum_actions.js";
import useOnboarding from "../../hooks/use-onboarding";
import type { OnboardingSegment } from "../../models/onboarding";
import styles from "./OnboardingStepper.module.css";
import Card from "../UI/Card.tsx";
import ButtonCard from "../UI/ButtonCard.tsx";
import Avatar from "../UI/Avatar.tsx";

type TablerIcon = ComponentType<IconProps & RefAttributes<SVGSVGElement>>;

type StepKey = "profile" | "property" | "tenant";
type PreviewIconKey = "user" | "house" | "users";

interface StepDef {
  key: StepKey;
  title: string;
  doneTitle: string;
  progressLabel: string;
  shortDesc: string;
  previewStrong: string;
  previewText: string;
  previewIcon: PreviewIconKey;
  benefits: string[];
  duration: string;
  cta: string;
  path: string;
}

interface FeatureDef {
  label: string;
  unlockAfter: number;
  activeAfter: number | null;
  path?: string;
}

interface SegmentDef {
  key: OnboardingSegment;
  icon: TablerIcon;
  title: string;
  desc: string;
  gain: string;
}

interface OnboardingStepperProps {
  hasProfile: boolean;
  hasProperty: boolean;
  hasTenant: boolean;
}

const LABEL_COLORS: Record<StepStatus, Color> = {
  done: "neutral",
  active: "primary",
  pending: "muted",
};

const PREVIEW_ICONS: Record<PreviewIconKey, TablerIcon> = {
  user: IconUser,
  house: IconHome,
  users: IconUsers,
};

const STEPS: StepDef[] = [
  {
    key: "profile",
    title: "Créez votre profil bailleur",
    doneTitle: "Profil bailleur créé",
    progressLabel: "Profil bailleur",
    shortDesc: "Identité, coordonnées, mode de détention",
    previewStrong: "Votre profil pré-remplit tous vos documents.",
    previewText:
      "Renseignez votre identité une seule fois — nom, adresse, mode de détention (SCI, indivision, nom propre). Ces données alimentent automatiquement chaque bail et quittance.",
    previewIcon: "user",
    benefits: [
      "Génération automatique de vos baux et documents officiels (bail nu, meublé, mobilité)",
      "Coordonnées propriétaire pré-remplies sur chaque document — aucune saisie répétitive",
      "Gestion multi-profils si vous possédez en SCI et en nom propre",
    ],
    duration: "2 minutes",
    cta: "Créer mon profil",
    path: "/bailleur/profiles",
  },
  {
    key: "property",
    title: "Ajoutez votre premier bien",
    doneTitle: "Bien ajouté",
    progressLabel: "Premier bien",
    shortDesc: "Adresse, type de location, loyer mensuel",
    previewStrong: "Votre bien structure toute votre gestion.",
    previewText:
      "Renseignez le type de bien, l'adresse et le nombre de pièces une seule fois — ces données alimentent automatiquement chaque bail, chaque quittance et votre suivi financier.",
    previewIcon: "house",
    benefits: [
      "Bail généré au bon format selon le type de location (nu, meublé, mobilité, LMNP)",
      "Suivi comptable en temps réel — loyers perçus, charges déductibles et récupérables",
      "Stockage de tous vos documents (diagnostics, DPE) repris automatiquement dans le bail",
    ],
    duration: "3 minutes",
    cta: "Ajouter mon bien",
    path: "/bailleur/properties/add",
  },
  {
    key: "tenant",
    title: "Rattachez votre premier locataire",
    doneTitle: "Locataire rattaché",
    progressLabel: "Premier locataire",
    shortDesc: "Nom, email, date d'entrée, montant du bail",
    previewStrong: "C'est ici que Qalimo prend vraiment le relais.",
    previewText:
      "Ajoutez votre locataire en 3 minutes — nom, email, date d'entrée. Qalimo génère votre bail, programme vos quittances et surveille les paiements à votre place dès ce soir.",
    previewIcon: "users",
    benefits: [
      "Quittances générées et envoyées automatiquement au locataire chaque 1er du mois",
      "Relances automatiques en cas de retard de paiement — vous n'avez plus rien à faire",
      "Historique complet des loyers encaissés, prêt pour votre déclaration fiscale",
    ],
    duration: "3 minutes",
    cta: "Ajouter mon locataire",
    path: "/bailleur/tenants/add",
  },
];

/*
 * États des pastilles : gris = verrouillé, bleu = débloqué à configurer
 * (cliquable), vert = actif. unlockAfter/activeAfter = nombre d'étapes
 * terminées requises.
 */
const FEATURES: FeatureDef[] = [
  { label: "Compte actif", unlockAfter: 0, activeAfter: 0 },
  {
    label: "Baux automatiques",
    unlockAfter: 0,
    activeAfter: 1,
    path: "/bailleur/profiles",
  },
  {
    label: "Quittances automatiques",
    unlockAfter: 2,
    activeAfter: null,
    path: "/bailleur/profiles",
  },
  {
    label: "Suivi des loyers",
    unlockAfter: 2,
    activeAfter: null,
    path: "/bailleur/finances",
  },
  {
    label: "Relances impayés",
    unlockAfter: 2,
    activeAfter: null,
    path: "/bailleur/profiles",
  },
  { label: "Dashboard complet", unlockAfter: 2, activeAfter: null },
];

const SEGMENTS: SegmentDef[] = [
  {
    key: "loue",
    icon: IconKey,
    title: "Mon logement est déjà loué",
    desc: "Un locataire est en place et paie un loyer chaque mois.",
    gain: "On automatise vos quittances dès ce soir",
  },
  {
    key: "recherche",
    icon: IconSearch,
    title: "Je cherche un locataire",
    desc: "Le logement est vacant, vous recevez ou attendez des candidatures.",
    gain: "On centralise vos dossiers candidats",
  },
  {
    key: "entree",
    icon: IconCalendarCheck,
    title: "Un locataire arrive bientôt",
    desc: "L'entrée est prévue : bail à signer, état des lieux à préparer.",
    gain: "On prépare l'entrée sereinement",
  },
];

const AVATARS: string[] = ["TM", "SB", "LP", "AC"];

export default function OnboardingStepper({
  hasProfile,
  hasProperty,
  hasTenant,
}: OnboardingStepperProps) {
  const nav = useNavigate();
  const { setSegment } = useOnboarding();
  const toast = useToast();

  // Seul « déjà loué » enchaîne sur le formulaire locataire. « Je cherche »
  // et « arrive bientôt » restent sur le tableau de bord, où la checklist des
  // missions du segment prend le relais.
  async function pickSegment(segment: OnboardingSegment) {
    datadogRum.addAction(RumActions.ONBOARDING_SEGMENT_CHOSEN, { segment });
    try {
      await setSegment(segment);
    } catch (error) {
      // Sans segment enregistré la suite de l'onboarding ne s'adapte pas :
      // on signale l'échec plutôt que d'enchaîner sur l'étape suivante.
      toast.error({
        title: "Impossible d'enregistrer votre situation",
        description: handleAPIError(error),
      });
      return;
    }
    if (segment === "loue") {
      nav("/bailleur/tenants/add");
    }
  }

  const stepStatus: StepStatus[] = [
    hasProfile ? "done" : "active",
    hasProperty ? "done" : hasProfile ? "active" : "pending",
    hasTenant ? "done" : hasProperty ? "active" : "pending",
  ];

  const nbDone = stepStatus.filter((status) => status === "done").length;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Title as="h1" variant="mega" weight="bold">
          Bienvenue sur Qalimo.
        </Title>
        <Text>
          Configurez votre espace en 3 étapes pour débloquer toutes les
          fonctionnalités.
        </Text>
      </div>

      <Card className={styles.progressSteps}>
        {STEPS.map((step, i) => {
          const status = stepStatus[i];
          return (
            <div key={step.key} className={styles.psStep}>
              <StepBadge status={status} number={i + 1} />
              <div className={styles.psLabel}>
                <Text as="div" weight="medium" color={LABEL_COLORS[status]}>
                  Étape {i + 1}
                </Text>
                <Text as="div" weight="bold" color={LABEL_COLORS[status]}>
                  {step.progressLabel}
                </Text>
              </div>
            </div>
          );
        })}
      </Card>

      <Card>
        <Text as="div" weight="bold" className={styles.unlockTitle}>
          Fonctionnalités débloquées
        </Text>
        <div className={styles.featuresRow}>
          {FEATURES.map((feat) => {
            const isActive =
              feat.activeAfter !== null && nbDone >= feat.activeAfter;
            const isUnlocked = !isActive && nbDone >= feat.unlockAfter;
            const path = feat.path;
            const isClickable = isUnlocked && !!path;
            const Pill = isClickable ? "button" : "div";
            return (
              <Pill
                key={feat.label}
                type={isClickable ? "button" : undefined}
                onClick={isClickable ? () => nav(path) : undefined}
                className={classnames(
                  styles.featPill,
                  isActive && styles.pillActive,
                  isUnlocked && styles.pillUnlocked,
                )}
              >
                <span className={styles.pillDot} />
                <Text
                  as="span"
                  weight={isActive || isUnlocked ? "bold" : "medium"}
                >
                  {feat.label}
                </Text>
              </Pill>
            );
          })}
        </div>
      </Card>

      {STEPS.map((step, i) => {
        const status = stepStatus[i];
        const isActive = status === "active";
        const isDone = status === "done";
        const PreviewIcon = PREVIEW_ICONS[step.previewIcon];

        return (
          <Card
            key={step.key}
            className={classnames(styles.step, styles[status])}
          >
            <div className={styles.stepHeader}>
              <StepBadge status={status} number={i + 1} />
              <div className={styles.stepHeaderText}>
                <Text
                  weight={isDone ? "bold" : "medium"}
                  color={STEP_TITLE_COLORS[status]}
                >
                  {isDone ? step.doneTitle : step.title}
                </Text>
                <Text color={STEP_DESC_COLORS[status]}>{step.shortDesc}</Text>
              </div>
              <div className={styles.stepRight}>
                {isDone ? (
                  <Status
                    as="span"
                    color="success"
                    weight="bold"
                    icon={IconCheck}
                    text="Terminé"
                  />
                ) : (
                  <Text as="span" color="muted" className={styles.timeBadge}>
                    <IconClock size={13} />
                    Environ {step.duration}
                  </Text>
                )}
              </div>
            </div>

            {isActive && (
              <div className={styles.stepBody}>
                {step.key === "tenant" ? (
                  <>
                    <div>
                      <Text weight="bold">
                        Dans quelle situation êtes-vous ?
                      </Text>
                      <Text color="muted">
                        Votre réponse adapte les prochaines étapes, modifiable à
                        tout moment.
                      </Text>
                    </div>
                    <div className={styles.segStack}>
                      {SEGMENTS.map((segment) => (
                        <ButtonCard
                          key={segment.key}
                          onClick={() => pickSegment(segment.key)}
                        >
                          <div className={styles.segRow}>
                            <span className={styles.segIcon}>
                              <segment.icon size={24} stroke={1.5} />
                            </span>
                            <span className={styles.segBody}>
                              <Text as="div" weight="bold">
                                {segment.title}
                              </Text>
                              <Text as="div" color="muted">
                                {segment.desc}
                              </Text>
                            </span>
                            <Text
                              as="span"
                              weight="bold"
                              color="primary"
                              className={styles.segGain}
                            >
                              {segment.gain}
                            </Text>
                          </div>
                        </ButtonCard>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.previewBlock}>
                      <div className={styles.previewIcon}>
                        <PreviewIcon size={18} color="white" />
                      </div>
                      <Text as="div">
                        <Text as="span" weight="bold">
                          {step.previewStrong}
                        </Text>
                        <br />
                        {step.previewText}
                      </Text>
                    </div>

                    <div className={styles.benefits}>
                      {step.benefits.map((benefit) => (
                        <Text
                          as="div"
                          key={benefit}
                          className={styles.benefitRow}
                        >
                          <IconCheck
                            className={styles.benefitIcon}
                            size={14}
                            stroke={2.5}
                          />
                          {benefit}
                        </Text>
                      ))}
                    </div>

                    <Button onClick={() => nav(step.path)}>{step.cta}</Button>
                  </>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <Card padding="sm" className={styles.socialCard}>
        <div className={styles.avatars}>
          {AVATARS.map((initials) => (
            <Avatar key={initials} initials={initials} />
          ))}
        </div>
        <Text as="div">
          <Text as="div" className={styles.stars}>
            ★★★★★
          </Text>
          <Text as="span" weight="bold">
            3 200+ bailleurs
          </Text>{" "}
          ont configuré leur espace en moins de 10 minutes.
          <br />
          <Text as="span" color="muted">
            "J'ai gagné des heures dès le premier mois." — Sophie L., 4 biens
            gérés sur Qalimo
          </Text>
        </Text>
      </Card>

      <div className={styles.footer}>
        <Text color="muted">Besoin d'un coup de main pour démarrer ?</Text>
        <div className={styles.footerLinks}>
          <a
            href="https://www.qalimo.fr/tutoriels/"
            target="_blank"
            rel="noreferrer"
          >
            Voir les tutoriels vidéo
          </a>
          <a
            href="https://calendly.com/app-qalimo/30min"
            target="_blank"
            rel="noreferrer"
          >
            Réserver un appel avec un expert
          </a>
        </div>
      </div>
    </div>
  );
}
